import React, { useState } from 'react';

type Question = {
  question: string;
  answer: React.ReactNode;
};

type Section = {
  title: string;
  faqs: Question[];
};

const faqData: Section[] = [
  {
    title: "Getting Started",
    faqs: [
      {
        question: "How does Misu Capital work?",
        answer: (
          <>
            We provide a marketplace of vetted financial advisors that you can browse and connect with directly.
            Simply explore advisor profiles, review their experience and specializations, and reach out to the advisor
            who best fits your needs. You can book consultation calls directly with any advisor on our platform.
          </>
        )
      },
      {
        question: "Is there a fee to use Misu Capital?",
        answer: (
          <>
            There's no fee to join our platform or to have initial consultation calls with advisors. You only pay the advisor's fees once you decide to work with them.
          </>
        )
      },
      {
        question: "How quickly can I get connected with an advisor?",
        answer: (
          <>
            You can browse advisor profiles and connect with them immediately. Most advisors respond within 24 hours, and consultation calls can typically be scheduled within 2–3 business days.
          </>
        )
      }
    ]
  },
  {
    title: "About Our Advisors",
    faqs: [
      {
        question: "How do you get your financial advisors?",
        answer: (
          <ul className="list-disc pl-6 space-y-1 text-gray-600">
            <li>Minimum 5+ years of experience in financial planning</li>
            <li>Right certifications and qualifications</li>
            <li>Clean regulatory record with no complaints</li>
            <li>Positive client references</li>
            <li>Commitment to fiduciary responsibility</li>
          </ul>
        )
      },
      {
        question: "What if I don't like my chosen advisor?",
        answer: (
          <>
            You can easily browse our platform and connect with a different advisor. You have complete control over choosing and switching advisors.
          </>
        )
      },
      {
        question: "Are your advisors regulated?",
        answer: (
          <>
            Yes, all our advisors are registered with appropriate regulatory bodies (AMFI, SEBI, IRDAI) and maintain active licenses.
          </>
        )
      },
      {
        question: "What if I have a complaint about my advisor?",
        answer: (
          <>
            We have a dedicated grievance redressal process. Contact our support and we'll help resolve issues quickly and fairly.
          </>
        )
      },
      {
        question: "How often should I meet with my advisor?",
        answer: (
          <>
            Most clients benefit from quarterly reviews and annual planning sessions. You can schedule additional meetings anytime.
          </>
        )
      },
      {
        question: "What if I need advice urgently?",
        answer: (
          <>
            Most advisors offer quick consultations for urgent matters. Some also provide WhatsApp/email support between scheduled calls.
          </>
        )
      }
    ]
  },
  {
    title: "Special Situations",
    faqs: [
      {
        question: "Do you work with NRIs?",
        answer: (
          <>
            Yes, we have advisors who specialize in NRI financial planning, tax optimization, repatriation, and cross-border investment.
          </>
        )
      },
      {
        question: "What if I'm just starting my career?",
        answer: (
          <>
            We have advisors who specialize in working with young professionals, helping them invest and build wealth from scratch.
          </>
        )
      },
      {
        question: "Do you help with business financial planning?",
        answer: (
          <>
            Yes, we have advisors experienced in business financial planning, including loans, cash flow, and succession planning.
          </>
        )
      }
    ]
  }
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenIndex(prev => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-gray-100 px-4 py-16">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-14">
          Frequently Asked Questions
        </h1>

        {faqData.map((section, sectionIdx) => (
          <div key={sectionIdx} className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6">{section.title}</h2>
            <div className="space-y-4">
              {section.faqs.map((faq, faqIdx) => {
                const id = `${sectionIdx}-${faqIdx}`;
                const isOpen = openIndex === id;

                return (
                  <div
                    key={id}
                    className="border border-gray-200 rounded-lg shadow-sm bg-white transition-all"
                  >
                    <button
                      onClick={() => toggle(id)}
                      className="w-full flex justify-between items-center px-5 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-gray-800 font-medium text-left">{faq.question}</span>
                      <span
                        className={`text-gray-500 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      >
                        ▼
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 text-sm text-gray-600 border-t border-gray-200 bg-gray-50 animate-fadeIn">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact */}
        <div className="mt-16 text-center border-t pt-6 text-gray-700">
          <p className="mb-1 font-medium">Still have questions?</p>
          <p>
            Call us:{' '}
            <a href="tel:+917470818049" className="text-blue-600 hover:underline">
              +91-7470818049
            </a>
            <br />
            Email:{' '}
            <a href="mailto:misucapital@gmail.com" className="text-blue-600 hover:underline">
              misucapital@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
